import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Leaf, Factory, TrendingDown, Flame, DollarSign, TreeDeciduous, Wind } from 'lucide-react';
import { Link } from 'react-router-dom';

const BriquetteInfoPage = () => {
  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <div className="mb-16 text-center">
          <h1 className="text-6xl md:text-8xl font-bold tracking-tighter uppercase leading-[0.9] mb-4 font-heading">
            What Are <span className="text-lime-400">Briquettes</span>?
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-zinc-300 max-w-3xl mx-auto">
            Understanding biomass briquettes, their manufacturing, and why they're the future of sustainable cooking fuel
          </p>
        </div>

        {/* What Are Briquettes Section */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-4">
                <span className="text-xs font-mono tracking-widest uppercase text-lime-400 bg-lime-400/10 border border-lime-400/20 px-3 py-1.5 inline-block">
                  SUSTAINABLE FUEL
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase font-heading mb-6">
                Biomass Briquettes Explained
              </h2>
              <div className="space-y-4 text-zinc-300 leading-relaxed">
                <p>
                  <strong className="text-white">Biomass briquettes</strong> are compressed blocks of organic materials like sawdust, agricultural waste, rice husk, and wood chips. Through high-pressure compression without any chemical binders, these materials are transformed into dense, uniform fuel blocks.
                </p>
                <p>
                  Our <strong className="text-lime-400">90mm diameter briquettes</strong> are specifically engineered for cooking applications, providing consistent heat output of <strong>3800-4200 kcal/kg</strong> - matching LPG's energy efficiency.
                </p>
                <p>
                  Unlike firewood or coal, briquettes burn cleaner, produce minimal ash (3-5%), and maintain steady heat for 3-4 hours per kilogram - making them ideal for households, restaurants, and industrial kitchens.
                </p>
              </div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-8">
              <img 
                src="https://images.unsplash.com/photo-1675758575377-90f7189761e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="Biomass Briquettes" 
                className="w-full h-auto border border-zinc-700"
              />
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <Leaf className="w-8 h-8 text-lime-400 mx-auto mb-2" />
                  <p className="text-xs uppercase tracking-wider text-zinc-400">100% Natural</p>
                </div>
                <div className="text-center">
                  <Flame className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-xs uppercase tracking-wider text-zinc-400">High Energy</p>
                </div>
                <div className="text-center">
                  <Wind className="w-8 h-8 text-lime-400 mx-auto mb-2" />
                  <p className="text-xs uppercase tracking-wider text-zinc-400">Low Smoke</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Manufacturing Process */}
        <section className="mb-20">
          <div className="mb-12 text-center">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              Manufacturing <span className="text-lime-400\">Process</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              From agricultural waste to premium cooking fuel - our eco-friendly production process
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors">
              <div className="w-14 h-14 bg-lime-400 text-black font-bold text-2xl flex items-center justify-center mb-4">
                1
              </div>
              <h3 className="font-heading text-xl font-bold uppercase mb-3\">Collection</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Agricultural waste (sawdust, rice husk, wood chips) collected from local mills and farms
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors">
              <div className="w-14 h-14 bg-lime-400 text-black font-bold text-2xl flex items-center justify-center mb-4">
                2
              </div>
              <h3 className="font-heading text-xl font-bold uppercase mb-3">Drying</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Raw material dried to reduce moisture content to 8-12% for optimal compression
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors">
              <div className="w-14 h-14 bg-lime-400 text-black font-bold text-2xl flex items-center justify-center mb-4">
                3
              </div>
              <h3 className="font-heading text-xl font-bold uppercase mb-3">Compression</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                High-pressure machines (1000-1200 kg/cm²) compress material into dense 90mm cylinders
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors">
              <div className="w-14 h-14 bg-lime-400 text-black font-bold text-2xl flex items-center justify-center mb-4">
                4
              </div>
              <h3 className="font-heading text-xl font-bold uppercase mb-3">Packaging</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Cooled briquettes packed in 25kg, 50kg, 100kg, 150kg bags for easy handling
              </p>
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 p-8">
            <div className="flex items-center gap-3 mb-6">
              <Factory className="w-8 h-8 text-lime-400" />
              <h3 className="font-heading text-2xl font-bold uppercase\">Key Manufacturing Benefits</h3>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <li className="flex items-start gap-3">
                <span className="text-lime-400 text-xl">✓</span>
                <span className="text-zinc-300">No chemical binders or additives - pure natural compression</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-lime-400 text-xl">✓</span>
                <span className="text-zinc-300">Converts agricultural waste into valuable energy source</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-lime-400 text-xl">✓</span>
                <span className="text-zinc-300">Uniform size ensures consistent burning and heat output</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-lime-400 text-xl">✓</span>
                <span className="text-zinc-300">Reduces deforestation by utilizing existing biomass waste</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Cost Comparison */}
        <section className="mb-20">
          <div className="mb-12 text-center">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              Cost <span className="text-lime-400">Comparison</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Real savings analysis: Briquettes vs Firewood vs LPG
            </p>
          </div>

          {/* Comparison Table */}
          <div className="bg-zinc-900 border border-zinc-800 overflow-hidden mb-12">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700 bg-zinc-800">
                  <th className="py-4 px-6 text-left font-heading uppercase text-lime-400\">Factor</th>
                  <th className="py-4 px-6 text-left font-heading uppercase text-lime-400\">Briquettes</th>
                  <th className="py-4 px-6 text-left font-heading uppercase\">Firewood</th>
                  <th className="py-4 px-6 text-left font-heading uppercase\">LPG</th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                <tr className="border-b border-zinc-800">
                  <td className="py-4 px-6 font-semibold">Price per 25kg</td>
                  <td className="py-4 px-6 text-lime-400 font-bold\">₹375</td>
                  <td className="py-4 px-6\">₹400-500</td>
                  <td className="py-4 px-6\">₹1,100 (14.2kg)</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-4 px-6 font-semibold">Energy (kcal/kg)</td>
                  <td className="py-4 px-6 text-lime-400 font-bold">4000</td>
                  <td className="py-4 px-6">3500-4500</td>
                  <td className="py-4 px-6">11,000</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-4 px-6 font-semibold">Burn Time (per kg)</td>
                  <td className="py-4 px-6 text-lime-400 font-bold">3-4 hours</td>
                  <td className="py-4 px-6">1-2 hours</td>
                  <td className="py-4 px-6">Variable</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-4 px-6 font-semibold">Smoke Level</td>
                  <td className="py-4 px-6 text-lime-400 font-bold">Very Low</td>
                  <td className="py-4 px-6 text-red-400">High</td>
                  <td className="py-4 px-6">None</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-4 px-6 font-semibold">Ash Content</td>
                  <td className="py-4 px-6 text-lime-400 font-bold">3-5%</td>
                  <td className="py-4 px-6">8-15%</td>
                  <td className="py-4 px-6">0%</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-4 px-6 font-semibold">Availability</td>
                  <td className="py-4 px-6 text-lime-400 font-bold">Same-day</td>
                  <td className="py-4 px-6">Variable</td>
                  <td className="py-4 px-6 text-red-400">45-day wait</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-semibold">Storage</td>
                  <td className="py-4 px-6 text-lime-400 font-bold">Easy, compact</td>
                  <td className="py-4 px-6">Bulky, pest issues</td>
                  <td className="py-4 px-6">Safety clearance needed</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Monthly Cost Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-lime-400 to-lime-500 p-8 text-black">
              <DollarSign className="w-12 h-12 mb-4" />
              <h3 className="font-heading text-3xl font-bold uppercase mb-2">Briquettes</h3>
              <p className="text-4xl font-bold mb-2\">₹1,125</p>
              <p className="text-sm opacity-80">75kg monthly (household)</p>
              <div className="mt-6 pt-6 border-t border-black/20">
                <p className="text-sm font-semibold">Restaurant (600kg/month)</p>
                <p className="text-3xl font-bold">₹9,000</p>
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-8">
              <TreeDeciduous className="w-12 h-12 text-zinc-400 mb-4" />
              <h3 className="font-heading text-3xl font-bold uppercase mb-2 text-white">Firewood</h3>
              <p className="text-4xl font-bold mb-2 text-white">₹1,500</p>
              <p className="text-sm text-zinc-400">75kg monthly (household)</p>
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <p className="text-sm font-semibold text-white\">Restaurant (600kg/month)</p>
                <p className="text-3xl font-bold text-white">₹12,000</p>
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-8">
              <Flame className="w-12 h-12 text-zinc-400 mb-4" />
              <h3 className="font-heading text-3xl font-bold uppercase mb-2 text-white">LPG</h3>
              <p className="text-4xl font-bold mb-2 text-red-400">₹3,300</p>
              <p className="text-sm text-zinc-400">3 cylinders/month (household)</p>
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <p className="text-sm font-semibold text-white">Restaurant (30 cylinders/month)</p>
                <p className="text-3xl font-bold text-red-400">₹33,000</p>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-lime-400/10 border border-lime-400/20 p-8 text-center">
            <TrendingDown className="w-16 h-16 text-lime-400 mx-auto mb-4" />
            <h3 className="font-heading text-3xl font-bold uppercase mb-4 text-lime-400">
              Save Up to 73% on Fuel Costs
            </h3>
            <p className="text-zinc-300 text-lg max-w-3xl mx-auto mb-6">
              For a typical restaurant using 600kg fuel monthly, switching from LPG to briquettes saves 
              <strong className="text-lime-400"> ₹24,000 per month</strong> or 
              <strong className="text-lime-400"> ₹2.88 lakhs per year</strong>!
            </p>
            <Link
              to="/products"
              className="inline-block bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1"
              data-testid="cost-comparison-cta"
            >
              Start Saving Today
            </Link>
          </div>
        </section>

        {/* Environmental Impact */}
        <section className="mb-20">
          <div className="bg-zinc-900 border border-zinc-800 p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase font-heading mb-4">
                Environmental <span className="text-lime-400">Impact</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-lime-400/10 border border-lime-400/20 flex items-center justify-center mx-auto mb-4">
                  <TreeDeciduous className="w-8 h-8 text-lime-400" />
                </div>
                <h3 className="font-bold text-white mb-2">Zero Deforestation</h3>
                <p className="text-zinc-400 text-sm">Made from agricultural waste, not trees</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-lime-400/10 border border-lime-400/20 flex items-center justify-center mx-auto mb-4">
                  <Wind className="w-8 h-8 text-lime-400" />
                </div>
                <h3 className="font-bold text-white mb-2">70% Less Emissions</h3>
                <p className="text-zinc-400 text-sm">Compared to traditional firewood</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-lime-400/10 border border-lime-400/20 flex items-center justify-center mx-auto mb-4">
                  <Leaf className="w-8 h-8 text-lime-400" />
                </div>
                <h3 className="font-bold text-white mb-2">Carbon Neutral</h3>
                <p className="text-zinc-400 text-sm">Releases only absorbed CO₂</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-lime-400/10 border border-lime-400/20 flex items-center justify-center mx-auto mb-4">
                  <Factory className="w-8 h-8 text-lime-400" />
                </div>
                <h3 className="font-bold text-white mb-2">Waste Reduction</h3>
                <p className="text-zinc-400 text-sm">Converts waste to valuable energy</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-zinc-700 p-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase font-heading mb-6">
            Ready to Make the <span className="text-lime-400">Switch</span>?
          </h2>
          <p className="text-zinc-300 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of Bengaluru homes and restaurants saving money while protecting the environment
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/products"
              className="bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1"
            >
              Shop Products
            </Link>
            <Link
              to="/vendor-empanelment"
              className="bg-transparent border-2 border-zinc-700 text-white hover:border-lime-400 hover:text-lime-400 font-bold uppercase tracking-wider px-8 py-4 transition-colors"
            >
              Bulk Orders
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BriquetteInfoPage;
